/**
 * Formula engine — self-contained Excel-subset expression language.
 *
 * This folder is designed for extraction into a shared package (both the FE and
 * the api consume @foresightdata/metrics-calculator; this engine is meant to move
 * there). It must not import anything from the rest of this repo; npm packages
 * the api and metrics-calculator also depend on (temporal-polyfill) are fine.
 */

export type BinaryOp = '+' | '-' | '*' | '/' | '^' | '=' | '<>' | '<' | '<=' | '>' | '>=';
export type UnaryOp = '-' | '+';

/**
 * A function name as the tree stores it: uppercase, resolved against a
 * FormulaRegistry (see registry.ts) at parse, check and evaluation time.
 * Nothing in the tree says whether a name is a builtin or a registered one.
 */
export type FormulaFunctionName = string;

/** LAMBDA and LET parse to their own node kinds, never to a call. */
export type CallFunctionName = string;

/**
 * `field.ref` holds whatever the source text had between brackets: a display name
 * in editor input, a canonical key in stored formulas. Resolution between the two
 * is a tree transform (see transformFields), not a parser concern.
 *
 * Names: a `param` is a bare bound name — a LET name or a REDUCE accumulator —
 * spelled as declared (the parser matches uses case-insensitively). A `lambda`
 * carries its parameter list; the parser accepts exactly one today (REDUCE's
 * accumulator), the node shape leaves room for more.
 * `LET(name1, value1, …, calculation)` is its own node: the bindings in
 * declaration order (each value may use the names before it) and the body.
 *
 * Lists: `REDUCE(initial, [list], LAMBDA(acc, body))` is a call whose args are
 * exactly [expr, field, lambda]. Inside the body, `param` refers to the
 * accumulator and a field ref of the form "<list path>.<member>" reads the
 * current item — still a plain field node; scope decides how it resolves.
 * `SIZE([list])` is a call with exactly [field]; `INDEX([list], index, [member])`
 * exactly [field, expr, field], the member a "<list path>.<member>" ref of its list;
 * `SUM([list], [member])` exactly [field, field], the member a ref of its list too.
 * `KPI([kpi], [period], [scenario])` is exactly [field, field, field]: a KPI ref
 * (canonically the row's kpiData path) and two enum-like tokens (see kpi.ts).
 */
export type AstNode =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'field'; ref: string }
  | { kind: 'param'; name: string }
  | { kind: 'unary'; op: UnaryOp; operand: AstNode }
  | { kind: 'binary'; op: BinaryOp; left: AstNode; right: AstNode }
  | { kind: 'call'; fn: CallFunctionName; args: AstNode[] }
  | { kind: 'lambda'; params: string[]; body: AstNode }
  | { kind: 'let'; bindings: LetBinding[]; body: AstNode };

export interface LetBinding {
  name: string;
  value: AstNode;
}

/** Excel-style error values produced by evaluation (not thrown). #REF! is INDEX out of range, per Excel. */
export type FormulaErrorCode = '#DIV/0!' | '#NUM!' | '#VALUE!' | '#REF!' | '#ERROR!';

export class FormulaError {
  constructor(
    public readonly code: FormulaErrorCode,
    public readonly message?: string
  ) {}
}

export function isFormulaError(value: unknown): value is FormulaError {
  return value instanceof FormulaError;
}

/**
 * Thrown by tokenize/parse. `position` is a 0-based index into the source
 * text; `endPosition` (exclusive) extends it to the offending span so editors
 * can highlight a range rather than a point.
 *
 * `incomplete` marks the failures where the source simply ran out — an
 * unfinished call, a dangling operator, an unclosed "[" — as opposed to
 * something the parser tripped over. Typing more can still make an incomplete
 * formula valid, so an editor can treat it as work in progress rather than an
 * error. It is never a reason to accept the formula.
 */
export class FormulaParseError extends Error {
  public readonly endPosition: number;

  constructor(
    message: string,
    public readonly position: number,
    endPosition?: number,
    public readonly incomplete = false
  ) {
    super(message);
    this.name = 'FormulaParseError';
    this.endPosition = endPosition ?? position + 1;
  }
}

/** The types a formula value can have. Named as the app's PrimitiveType names them. */
export type ValueType = 'number' | 'string' | 'boolean' | 'date';

/**
 * What a canonical ref denotes, as far as the type rules care. `label` is
 * the name to show in messages (a display name); it defaults to the path.
 * A scalar carries its value type; a member carries its scalar `type` too,
 * so SUM can insist on numbers and INDEX can yield the member's type. A kpi
 * carries the canonical period ('Month' | 'Quarter' | 'Year') and section
 * ('actual' | 'budget') tokens its config supports, so KPI() can insist on
 * an available combination.
 */
export type FieldType =
  | { kind: 'scalar'; type: ValueType; label?: string }
  | { kind: 'list'; label?: string }
  | { kind: 'member'; list: string; type: ValueType; label?: string; listLabel?: string }
  | { kind: 'kpi'; label?: string; periods: string[]; sections: string[] }
  | { kind: 'other'; type: string; label?: string };

export type FieldTypeResolver = (path: string) => FieldType | undefined;

/**
 * A type rule violation. `ref` is the offending canonical ref and `fieldIndex`
 * its position among the tree's field refs in source order, so an editor can
 * underline that occurrence even when the same ref appears more than once.
 * `node` is the offending node when the rule is about an expression rather
 * than a ref; spanOf(node) then locates it in the source.
 */
export class FormulaTypeError extends Error {
  constructor(
    message: string,
    public readonly ref: string,
    public readonly fieldIndex: number,
    public readonly node?: AstNode
  ) {
    super(message);
    this.name = 'FormulaTypeError';
  }
}
